// ============================================================
// Data Hub Query Interface – client-side interactions
// ============================================================
import L from "leaflet";
import * as bootstrap from "bootstrap";

// Fix missing marker icons when bundling
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon   from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl:       markerIcon,
  shadowUrl:     markerShadow,
});

// ------------------------------------------------------------------
// Map setup
// ------------------------------------------------------------------
let map = null;
let marker = null;
let radiusCircle = null;
let boundingBox = null;
let boxDrawMode = false;
let boxDrawStart = null;
let boxDrawPreview = null;

const DEFAULT_CENTER = [53.5, -113.5]; // Alberta center
const DEFAULT_ZOOM   = 5;

function initMap() {
  const el = document.getElementById("dh-map");
  if (!el || el.dataset.leafletInitialized === "true") return;
  el.dataset.leafletInitialized = "true";

  map = L.map(el, { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, zoomControl: true });

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap contributors",
  }).addTo(map);

  // Click on map → fill coordinate inputs (only in point mode)
  map.on("click", (e) => {
    if (boxDrawMode) return;               // ignore clicks while drawing a box
    if (getCoordMode() === "bbox") return; // ignore clicks in bbox mode
    setCoordinateInputs(e.latlng.lat, e.latlng.lng);
    placeMarker(e.latlng.lat, e.latlng.lng);
  });
}

function placeMarker(lat, lng, popupText) {
  if (!map) return;
  // Placing a point clears any bounding box
  clearBoundingBox();
  const latlng = [lat, lng];

  if (marker) {
    marker.setLatLng(latlng);
  } else {
    marker = L.marker(latlng).addTo(map);
  }

  if (popupText) {
    marker.bindPopup(popupText).openPopup();
  }

  drawRadiusCircle(lat, lng);
  map.flyTo(latlng, Math.max(map.getZoom(), 9), { duration: 0.8 });
}

function drawRadiusCircle(lat, lng) {
  const radiusKm = parseFloat(document.getElementById("dh-radius")?.value) || 50;
  if (radiusCircle) map.removeLayer(radiusCircle);
  radiusCircle = L.circle([lat, lng], {
    radius: radiusKm * 1000,
    color: "#3388ff",
    fillColor: "#3388ff",
    fillOpacity: 0.08,
    weight: 1.5,
    dashArray: "6 4",
  }).addTo(map);
}

function clearMapMarkers() {
  if (marker)       { map.removeLayer(marker);       marker = null; }
  if (radiusCircle) { map.removeLayer(radiusCircle);  radiusCircle = null; }
}

function clearBoundingBox() {
  if (boundingBox)   { map.removeLayer(boundingBox);     boundingBox = null; }
  if (boxDrawPreview) { map.removeLayer(boxDrawPreview); boxDrawPreview = null; }
  boxDrawStart = null;
  updateBoxCoordsDisplay(null);
}

function resetMapView() {
  if (!map) return;
  clearMapMarkers();
  clearBoundingBox();
  disableBoxDrawMode();
  map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 0.6 });
}

// ------------------------------------------------------------------
// Bounding-box draw mode
// ------------------------------------------------------------------
function enableBoxDrawMode() {
  if (!map) return;
  boxDrawMode = true;
  map.dragging.disable();
  map.getContainer().style.cursor = "crosshair";

  const btn = document.getElementById("dh-map-draw-box");
  if (btn) {
    btn.classList.remove("btn-outline-secondary");
    btn.classList.add("btn-success", "active");
  }

  map.on("mousedown", onBoxMouseDown);
  map.on("mousemove", onBoxMouseMove);
  map.on("mouseup",   onBoxMouseUp);
}

function disableBoxDrawMode() {
  if (!map) return;
  boxDrawMode = false;
  boxDrawStart = null;
  map.dragging.enable();
  map.getContainer().style.cursor = "";

  const btn = document.getElementById("dh-map-draw-box");
  if (btn) {
    btn.classList.remove("btn-success", "active");
    btn.classList.add("btn-outline-secondary");
  }

  map.off("mousedown", onBoxMouseDown);
  map.off("mousemove", onBoxMouseMove);
  map.off("mouseup",   onBoxMouseUp);

  if (boxDrawPreview) {
    map.removeLayer(boxDrawPreview);
    boxDrawPreview = null;
  }
}

function onBoxMouseDown(e) {
  if (!boxDrawMode) return;
  L.DomEvent.preventDefault(e.originalEvent);
  boxDrawStart = e.latlng;
  // Remove previous box
  if (boundingBox)   { map.removeLayer(boundingBox);     boundingBox = null; }
  if (boxDrawPreview) { map.removeLayer(boxDrawPreview); boxDrawPreview = null; }
}

function onBoxMouseMove(e) {
  if (!boxDrawMode || !boxDrawStart) return;
  const bounds = L.latLngBounds(boxDrawStart, e.latlng);
  if (boxDrawPreview) {
    boxDrawPreview.setBounds(bounds);
  } else {
    boxDrawPreview = L.rectangle(bounds, {
      color: "#198754",
      weight: 2,
      dashArray: "6 4",
      fillColor: "#198754",
      fillOpacity: 0.1,
    }).addTo(map);
  }
}

function onBoxMouseUp(e) {
  if (!boxDrawMode || !boxDrawStart) return;
  const bounds = L.latLngBounds(boxDrawStart, e.latlng);

  // Ignore tiny accidental clicks (less than ~1 px)
  const p1 = map.latLngToContainerPoint(boxDrawStart);
  const p2 = map.latLngToContainerPoint(e.latlng);
  if (Math.abs(p1.x - p2.x) < 4 && Math.abs(p1.y - p2.y) < 4) {
    if (boxDrawPreview) { map.removeLayer(boxDrawPreview); boxDrawPreview = null; }
    boxDrawStart = null;
    return;
  }

  // Remove preview, draw final box
  if (boxDrawPreview) { map.removeLayer(boxDrawPreview); boxDrawPreview = null; }
  boundingBox = L.rectangle(bounds, {
    color: "#198754",
    weight: 2,
    fillColor: "#198754",
    fillOpacity: 0.12,
  }).addTo(map);

  // Drawing a box clears any point marker
  clearMapMarkers();
  ["dh-lat", "dh-lng"].forEach((id) => { const el = document.getElementById(id); if (el) el.value = ""; });

  updateBoxCoordsDisplay(bounds);

  // Exit draw mode after drawing
  disableBoxDrawMode();
}

function updateBoxCoordsDisplay(bounds) {
  const ids = ["dh-box-nw-lat", "dh-box-nw-lng", "dh-box-ne-lat", "dh-box-ne-lng",
               "dh-box-sw-lat", "dh-box-sw-lng", "dh-box-se-lat", "dh-box-se-lng"];

  if (!bounds) {
    ids.forEach((id) => { const el = document.getElementById(id); if (el) el.value = ""; });
    return;
  }

  const nw = bounds.getNorthWest();
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const se = bounds.getSouthEast();

  const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val.toFixed(6); };
  set("dh-box-nw-lat", nw.lat); set("dh-box-nw-lng", nw.lng);
  set("dh-box-ne-lat", ne.lat); set("dh-box-ne-lng", ne.lng);
  set("dh-box-sw-lat", sw.lat); set("dh-box-sw-lng", sw.lng);
  set("dh-box-se-lat", se.lat); set("dh-box-se-lng", se.lng);
}

// ------------------------------------------------------------------
// Coord mode helpers (point vs bbox)
// ------------------------------------------------------------------
function getCoordMode() {
  const radio = document.querySelector('input[name="dhCoordMode"]:checked');
  return radio ? radio.value : "point";
}

function activatePointMode() {
  const pointSection = document.getElementById("dh-subsection-point");
  const bboxSection  = document.getElementById("dh-subsection-bbox");
  if (pointSection) pointSection.classList.remove("d-none");
  if (bboxSection)  bboxSection.classList.add("d-none");

  // Clear bounding box from map
  clearBoundingBox();
  disableBoxDrawMode();
}

function activateBBoxMode() {
  const pointSection = document.getElementById("dh-subsection-point");
  const bboxSection  = document.getElementById("dh-subsection-bbox");
  if (pointSection) pointSection.classList.add("d-none");
  if (bboxSection)  bboxSection.classList.remove("d-none");

  // Clear point marker & radius from map and inputs
  clearMapMarkers();
  ["dh-lat", "dh-lng"].forEach((id) => { const el = document.getElementById(id); if (el) el.value = ""; });
  const radius = document.getElementById("dh-radius"); if (radius) radius.value = "50";
}

// ------------------------------------------------------------------
// Coordinate helpers
// ------------------------------------------------------------------
function setCoordinateInputs(lat, lng) {
  const latEl = document.getElementById("dh-lat");
  const lngEl = document.getElementById("dh-lng");
  if (latEl) latEl.value = lat.toFixed(6);
  if (lngEl) lngEl.value = lng.toFixed(6);

  // Activate the coords tab if not already active
  const coordsTab = document.getElementById("dh-tab-coords");
  if (coordsTab) {
    const tab = new bootstrap.Tab(coordsTab);
    tab.show();
  }
}

// Called when lat/lng inputs change
function onCoordsChanged() {
  const lat = parseFloat(document.getElementById("dh-lat")?.value);
  const lng = parseFloat(document.getElementById("dh-lng")?.value);
  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    placeMarker(lat, lng, `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
  }
}

// ------------------------------------------------------------------
// Geocoding (Nominatim – free, no API key)
// ------------------------------------------------------------------
async function geocodePlace(query) {
  const resultsEl = document.getElementById("dh-geocode-results");
  if (!resultsEl) return;
  resultsEl.innerHTML = `<div class="text-muted small"><i class="bi bi-hourglass-split me-1"></i>Searching...</div>`;

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      new URLSearchParams({ q: query, format: "json", limit: 5, countrycodes: "ca" })
    );
    const data = await res.json();

    if (!data.length) {
      resultsEl.innerHTML = `<div class="text-muted small">No results found.</div>`;
      return;
    }

    const list = document.createElement("div");
    list.className = "list-group list-group-flush";
    data.forEach((place) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "list-group-item list-group-item-action py-2 small border rounded";
      item.innerHTML = `<i class="bi bi-geo-alt text-success me-1"></i>${place.display_name}`;
      item.addEventListener("click", () => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        setCoordinateInputs(lat, lon);
        placeMarker(lat, lon, place.display_name);
        resultsEl.innerHTML = "";
      });
      list.appendChild(item);
    });
    resultsEl.innerHTML = "";
    resultsEl.appendChild(list);
  } catch (err) {
    resultsEl.innerHTML = `<div class="text-danger small">Geocoding error: ${err.message}</div>`;
  }
}

// ------------------------------------------------------------------
// Legal Land Description → approximate coordinates
// Alberta DLS (Dominion Land Survey) rough conversion
// ------------------------------------------------------------------
function lldToLatLng(quarter, section, township, range, meridian) {
  // Meridian base longitudes (approximate)
  const meridianBase = { W4: -110.0, W5: -114.0, W6: -118.0 };
  const baseLng = meridianBase[meridian];
  if (!baseLng || !township || !range || !section) return null;

  // Each township ≈ 6 miles ≈ 9.66 km north from baseline (49°N)
  // Each range ≈ 6 miles ≈ 9.66 km wide
  const baseLat = 49.0;
  const townshipDeg = 0.08696; // ~6 miles in degrees latitude
  const rangeDeg    = 0.1333;  // ~6 miles in degrees longitude (at ~52°N)

  let lat = baseLat + (township - 0.5) * townshipDeg;
  let lng = baseLng - (range - 0.5) * rangeDeg;

  // Section offset within township (6×6 grid, numbered S-pattern)
  const sectionRow = Math.floor((section - 1) / 6);
  const sectionCol = (sectionRow % 2 === 0) ? (section - 1) % 6 : 5 - (section - 1) % 6;
  lat += (sectionRow + 0.5) * (townshipDeg / 6);
  lng -= (sectionCol + 0.5) * (rangeDeg / 6);

  // Quarter offset
  const quarterOffsets = {
    NE: [0.25, -0.25], NW: [0.25, 0.25],
    SE: [-0.25, -0.25], SW: [-0.25, 0.25],
  };
  const qOff = quarterOffsets[quarter] || [0, 0];
  lat += qOff[0] * (townshipDeg / 6);
  lng += qOff[1] * (rangeDeg / 6);

  return { lat, lng };
}

// ------------------------------------------------------------------
// Tag toggle
// ------------------------------------------------------------------
function initTagButtons() {
  document.querySelectorAll(".dh-tag-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      btn.classList.toggle("active");
      btn.classList.toggle("selected");
    });
  });
}

// ------------------------------------------------------------------
// Date presets
// ------------------------------------------------------------------
function initDatePresets() {
  document.querySelectorAll(".dh-date-preset").forEach((btn) => {
    btn.addEventListener("click", () => {
      // deactivate siblings
      document.querySelectorAll(".dh-date-preset").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");

      const endEl   = document.getElementById("dh-date-end");
      const startEl = document.getElementById("dh-date-start");
      const now = new Date();
      if (endEl) endEl.value = now.toISOString().slice(0, 10);

      // "All" preset – clear start date
      if (btn.dataset.preset === "all") {
        if (startEl) startEl.value = "";
        return;
      }

      const start = new Date(now);
      const days   = parseInt(btn.dataset.days, 10);
      const weeks  = parseInt(btn.dataset.weeks, 10);
      const months = parseInt(btn.dataset.months, 10);
      const years  = parseInt(btn.dataset.years, 10);

      if (!isNaN(days))   start.setDate(start.getDate() - days);
      if (!isNaN(weeks))  start.setDate(start.getDate() - weeks * 7);
      if (!isNaN(months)) start.setMonth(start.getMonth() - months);
      if (!isNaN(years))  start.setFullYear(start.getFullYear() - years);

      if (startEl) startEl.value = start.toISOString().slice(0, 10);
    });
  });
}

// ------------------------------------------------------------------
// Wire everything together
// ------------------------------------------------------------------
export function initDataHubQuery() {
  const panel = document.getElementById("dh-query-panel");
  if (!panel) return;

  initMap();
  initTagButtons();
  initDatePresets();

  // Coord-mode radio buttons (Point & Radius vs Bounding Box)
  document.getElementById("dh-coord-mode-point")?.addEventListener("change", () => activatePointMode());
  document.getElementById("dh-coord-mode-bbox")?.addEventListener("change", () => activateBBoxMode());

  // Coords input listeners
  ["dh-lat", "dh-lng", "dh-radius"].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("change", onCoordsChanged);
  });

  // Geocode button
  const geocodeBtn = document.getElementById("dh-geocode-btn");
  const placeInput = document.getElementById("dh-place-name");
  if (geocodeBtn && placeInput) {
    geocodeBtn.addEventListener("click", () => geocodePlace(placeInput.value.trim()));
    placeInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); geocodePlace(placeInput.value.trim()); }
    });
  }

  // LLD lookup
  const lldBtn = document.getElementById("dh-lld-lookup");
  if (lldBtn) {
    lldBtn.addEventListener("click", () => {
      const quarter  = document.getElementById("dh-lld-quarter")?.value;
      const section  = parseInt(document.getElementById("dh-lld-section")?.value, 10);
      const township = parseInt(document.getElementById("dh-lld-township")?.value, 10);
      const range    = parseInt(document.getElementById("dh-lld-range")?.value, 10);
      const meridian = document.getElementById("dh-lld-meridian")?.value;

      const result = lldToLatLng(quarter, section, township, range, meridian);
      if (result) {
        const label = `${quarter || ""} ${section}-${township}-${range} ${meridian}`.trim();
        setCoordinateInputs(result.lat, result.lng);
        placeMarker(result.lat, result.lng, `<b>LLD:</b> ${label}<br>${result.lat.toFixed(4)}, ${result.lng.toFixed(4)}`);
      } else {
        alert("Please fill in at least Section, Township, Range, and Meridian.");
      }
    });
  }

  // Draw Box toggle
  const drawBoxBtn = document.getElementById("dh-map-draw-box");
  if (drawBoxBtn) {
    drawBoxBtn.addEventListener("click", () => {
      if (boxDrawMode) {
        disableBoxDrawMode();
      } else {
        enableBoxDrawMode();
      }
    });
  }

  // Clear bounding box
  const clearBoxBtn = document.getElementById("dh-clear-box");
  if (clearBoxBtn) {
    clearBoxBtn.addEventListener("click", () => {
      clearBoundingBox();
    });
  }

  // Reset map view
  const resetMapBtn = document.getElementById("dh-map-reset");
  if (resetMapBtn) resetMapBtn.addEventListener("click", resetMapView);

  // My Location button – browser geolocation
  const myLocBtn = document.getElementById("dh-map-my-location");
  if (myLocBtn) {
    myLocBtn.addEventListener("click", () => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser.");
        return;
      }
      myLocBtn.disabled = true;
      myLocBtn.innerHTML = '<i class="bi bi-hourglass-split me-1"></i>Locating…';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setCoordinateInputs(latitude, longitude);
          placeMarker(latitude, longitude, `<b>Your Location</b><br>${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
          myLocBtn.disabled = false;
          myLocBtn.innerHTML = '<i class="bi bi-crosshair me-1"></i>My Location';
        },
        (err) => {
          alert(`Could not get your location: ${err.message}`);
          myLocBtn.disabled = false;
          myLocBtn.innerHTML = '<i class="bi bi-crosshair me-1"></i>My Location';
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }

  // Clear keyword
  const clearKw = document.getElementById("dh-clear-keyword");
  if (clearKw) {
    clearKw.addEventListener("click", () => {
      const kw = document.getElementById("dh-keyword");
      if (kw) { kw.value = ""; kw.focus(); }
    });
  }

  // Reset all filters
  const resetAll = document.getElementById("dh-reset-all");
  if (resetAll) {
    resetAll.addEventListener("click", () => {
      // keyword
      const kw = document.getElementById("dh-keyword");
      if (kw) kw.value = "";
      // tags
      document.querySelectorAll(".dh-tag-btn.active, .dh-tag-btn.selected").forEach((b) => {
        b.classList.remove("active", "selected");
      });
      // dates
      const ds = document.getElementById("dh-date-start");
      const de = document.getElementById("dh-date-end");
      if (ds) ds.value = "";
      if (de) de.value = "";
      document.querySelectorAll(".dh-date-preset.active").forEach((b) => b.classList.remove("active"));
      // coords
      ["dh-lat", "dh-lng"].forEach((id) => { const e = document.getElementById(id); if (e) e.value = ""; });
      const radius = document.getElementById("dh-radius"); if (radius) radius.value = "50";
      // lld
      ["dh-lld-quarter", "dh-lld-section", "dh-lld-township", "dh-lld-range", "dh-lld-meridian"].forEach((id) => {
        const e = document.getElementById(id); if (e) e.value = "";
      });
      // place
      const pn = document.getElementById("dh-place-name"); if (pn) pn.value = "";
      const gr = document.getElementById("dh-geocode-results"); if (gr) gr.innerHTML = "";
      // reset coord mode to Point & Radius
      const pointRadio = document.getElementById("dh-coord-mode-point");
      if (pointRadio) { pointRadio.checked = true; activatePointMode(); }
      // map & bounding box
      resetMapView();
    });
  }

  // Search button (prototype – scrolls to results & updates count)
  const searchBtn = document.getElementById("dh-search-btn");
  if (searchBtn) {
    searchBtn.addEventListener("click", () => {
      const resultsSection = document.getElementById("dh-results");
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Update mock result count
      const countEl = document.getElementById("dh-result-count");
      const mockCount = document.querySelectorAll(".dh-result-card").length;
      if (countEl) countEl.textContent = `Showing ${mockCount} datasets`;
    });
  }

  initResultsToolbar();
  initResultCardActions();
  initRelatedTags();

  // Invalidate map size after tab switch (Leaflet quirk)
  document.querySelectorAll('#dh-location-tabs button[data-bs-toggle="tab"]').forEach((tab) => {
    tab.addEventListener("shown.bs.tab", () => {
      if (map) setTimeout(() => map.invalidateSize(), 150);
    });
  });
}

// ------------------------------------------------------------------
// Results toolbar (sort, view toggle)
// ------------------------------------------------------------------
function initResultsToolbar() {
  // Sort buttons
  document.querySelectorAll(".dh-sort-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll(".dh-sort-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      // Prototype: just show a brief visual feedback
      const grid = document.getElementById("dh-results-grid");
      if (grid) {
        grid.style.opacity = "0.5";
        setTimeout(() => { grid.style.opacity = "1"; }, 300);
      }
    });
  });

  // View toggle (grid ↔ list)
  document.querySelectorAll(".dh-view-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll(".dh-view-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      const grid = document.getElementById("dh-results-grid");
      if (!grid) return;

      if (btn.dataset.view === "list") {
        // Switch cards to full-width list items
        grid.querySelectorAll(".col-md-6.col-xl-4").forEach((col) => {
          col.className = "col-12";
        });
        grid.querySelectorAll(".dh-result-card .card-body").forEach((body) => {
          body.classList.add("d-md-flex", "gap-3");
        });
      } else {
        // Restore grid layout
        grid.querySelectorAll(".col-12").forEach((col) => {
          col.className = "col-md-6 col-xl-4";
        });
        grid.querySelectorAll(".dh-result-card .card-body").forEach((body) => {
          body.classList.remove("d-md-flex", "gap-3");
        });
      }
    });
  });
}

// ------------------------------------------------------------------
// Result card actions (preview, cite, bookmark)
// ------------------------------------------------------------------
function initResultCardActions() {
  // Bookmark toggle
  document.querySelectorAll(".dh-btn-bookmark").forEach((btn) => {
    btn.addEventListener("click", () => {
      const icon = btn.querySelector("i");
      if (icon.classList.contains("bi-bookmark")) {
        icon.classList.replace("bi-bookmark", "bi-bookmark-fill");
        btn.classList.add("active");
        btn.title = "Remove bookmark";
      } else {
        icon.classList.replace("bi-bookmark-fill", "bi-bookmark");
        btn.classList.remove("active");
        btn.title = "Bookmark";
      }
    });
  });

  // Copy citation (prototype – copies card title as a mock citation)
  document.querySelectorAll(".dh-btn-cite").forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest(".dh-result-card");
      const title = card?.querySelector(".card-title")?.textContent || "Dataset";
      const doi = card?.querySelector(".bi-link-45deg")?.parentElement?.textContent?.trim() || "";
      const citation = `${title}. ${doi}. Accessed ${new Date().toISOString().slice(0, 10)}.`;

      navigator.clipboard.writeText(citation).then(() => {
        const original = btn.innerHTML;
        btn.innerHTML = '<i class="bi bi-check-lg"></i>';
        btn.classList.add("btn-success");
        btn.classList.remove("btn-outline-secondary");
        setTimeout(() => {
          btn.innerHTML = original;
          btn.classList.remove("btn-success");
          btn.classList.add("btn-outline-secondary");
        }, 1500);
      }).catch(() => {
        alert("Citation copied:\n" + citation);
      });
    });
  });

  // Preview button (prototype – simple alert)
  document.querySelectorAll(".dh-btn-preview").forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest(".dh-result-card");
      const title = card?.querySelector(".card-title")?.textContent || "Dataset";
      alert(`Preview for "${title}" would open here.\n\nThis will show a data table preview, column summary statistics, and spatial extent on the map.`);
    });
  });
}

// ------------------------------------------------------------------
// Related tags – click to add as search filter
// ------------------------------------------------------------------
function initRelatedTags() {
  document.querySelectorAll(".dh-related-tag").forEach((tag) => {
    tag.addEventListener("click", (e) => {
      e.preventDefault();
      const keyword = tag.dataset.tag;
      const kwInput = document.getElementById("dh-keyword");
      if (kwInput) {
        // Append to keyword search
        const current = kwInput.value.trim();
        kwInput.value = current ? `${current}, ${keyword}` : keyword;
      }
      // Visual feedback
      tag.classList.add("active");
      // Scroll up to search panel
      const panel = document.getElementById("dh-query-panel");
      if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
}
